/**
 * Offline audio store — persists downloaded shiur blobs in IndexedDB so
 * the player can play them without an internet connection.
 *
 * IDB was chosen over the Service Worker cache because audio streaming
 * relies on HTTP Range requests; storing the full blob and handing the
 * browser a `blob:` URL sidesteps the whole Range-handling problem.
 *
 * Storage schema:
 *   Store: 'audio'
 *     key   = drive_file_id (string)
 *     value = {
 *       id: drive_file_id,
 *       blob: Blob,
 *       size: number,
 *       mime: string,
 *       title: string,
 *       speaker: string,
 *       addedAt: ISO timestamp,
 *     }
 *
 *   Store: 'meta' — lightweight index for listOffline() lookups without
 *   pulling every blob into memory.
 */
import { openDB } from 'idb';

const DB_NAME = 'vaad-offline-v1';
const DB_VERSION = 1;
const AUDIO_STORE = 'audio';
const META_STORE = 'meta';

let _dbPromise = null;
const getDB = () => {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(AUDIO_STORE)) db.createObjectStore(AUDIO_STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(META_STORE))  db.createObjectStore(META_STORE,  { keyPath: 'id' });
    },
  });
  return _dbPromise;
};

/** Save a downloaded blob for future offline playback. */
export async function saveForOffline(recording, blob) {
  if (!recording || !recording.drive_file_id || !(blob instanceof Blob)) return false;
  try {
    const db = await getDB();
    const entry = {
      id: recording.drive_file_id,
      blob,
      size: blob.size,
      mime: blob.type || 'audio/mpeg',
      title: recording.title || '',
      speaker: recording.speaker || '',
      addedAt: new Date().toISOString(),
    };
    await db.put(AUDIO_STORE, entry);
    // Keep the meta store slim — no blob, just enough to power the
    // /downloads offline-availability chips without hydrating megabytes
    // of audio into memory.
    await db.put(META_STORE, {
      id: entry.id,
      size: entry.size,
      mime: entry.mime,
      title: entry.title,
      speaker: entry.speaker,
      addedAt: entry.addedAt,
    });
    return true;
  } catch (e) {
    // Quota exceeded or IDB not available — swallow so downloads still
    // succeed even if offline caching is unavailable.
    return false;
  }
}

/** Return a fresh Blob URL for offline playback, or null if not cached. */
export async function getOfflineAudioUrl(driveFileId) {
  if (!driveFileId) return null;
  try {
    const db = await getDB();
    const entry = await db.get(AUDIO_STORE, driveFileId);
    if (!entry || !entry.blob) return null;
    return URL.createObjectURL(entry.blob);
  } catch {
    return null;
  }
}

/** Cheap boolean check — doesn't touch the blob. */
export async function hasOfflineAudio(driveFileId) {
  if (!driveFileId) return false;
  try {
    const db = await getDB();
    const meta = await db.get(META_STORE, driveFileId);
    return !!meta;
  } catch {
    return false;
  }
}

/** List every recording available offline (meta only, no blobs). */
export async function listOfflineAudio() {
  try {
    const db = await getDB();
    return await db.getAll(META_STORE);
  } catch {
    return [];
  }
}

/** Remove a recording's offline copy. */
export async function removeOfflineAudio(driveFileId) {
  if (!driveFileId) return;
  try {
    const db = await getDB();
    await db.delete(AUDIO_STORE, driveFileId);
    await db.delete(META_STORE, driveFileId);
  } catch { /* ignore */ }
}

/** Total bytes cached — useful for the Downloads page storage widget. */
export async function offlineAudioTotalBytes() {
  const items = await listOfflineAudio();
  return items.reduce((sum, it) => sum + (it.size || 0), 0);
}
